import { Button, Select, Space } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import IncidentListTable from '../components/IncidentListTable';
import { listIncidents } from '../services/api';
import type { AssignmentFilter, CurrentUser, Incident, IncidentAttachment, IncidentTask } from '../types/incident';

interface Props {
  incidents: Incident[];
  attachmentsByIncident: Record<string, IncidentAttachment[]>;
  tasksByIncident: Record<string, IncidentTask[]>;
  currentUser: CurrentUser | null;
  assignmentFilter: AssignmentFilter;
  onAssignmentFilterChange: (filter: AssignmentFilter) => void;
}

const IncidentListPage = ({
  incidents,
  attachmentsByIncident,
  tasksByIncident,
  currentUser,
  assignmentFilter,
  onAssignmentFilterChange,
}: Props) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const assignmentParam = searchParams.get('assignment') || 'ALL';
  const fullTextQuery = searchParams.get('q')?.trim() || '';
  const [searchResults, setSearchResults] = useState<Incident[] | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    if (assignmentParam !== assignmentFilter) {
      onAssignmentFilterChange(assignmentParam);
    }
  }, [assignmentFilter, assignmentParam, onAssignmentFilterChange]);

  const handleAssignmentFilterChange = (filter: AssignmentFilter) => {
    onAssignmentFilterChange(filter);
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (filter === 'ALL') {
        next.delete('assignment');
      } else {
        next.set('assignment', filter);
      }
      return next;
    }, { replace: true });
  };

  useEffect(() => {
    if (!fullTextQuery) {
      setSearchResults(null);
      setSearchLoading(false);
      return;
    }

    let cancelled = false;
    setSearchLoading(true);
    setSearchResults(null);
    const timer = window.setTimeout(() => {
      listIncidents({ keyword: fullTextQuery })
        .then((items) => {
          if (!cancelled) setSearchResults(items);
        })
        .catch(() => {
          if (!cancelled) setSearchResults([]);
        })
        .finally(() => {
          if (!cancelled) setSearchLoading(false);
        });
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [fullTextQuery]);

  const displayedIncidents = useMemo(
    () => (fullTextQuery ? searchResults || [] : incidents),
    [fullTextQuery, incidents, searchResults]
  );

  return (
    <div className="page incidentListPage">
      <div className="pageToolbar">
        <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/incidents/new')}>
          {t('incident.list.actions.newIncident')}
        </Button>
      </div>
      <Space className="filterBar" wrap>
        <Select
          className="assignmentFilter"
          value={assignmentFilter}
          onChange={handleAssignmentFilterChange}
          options={[
            { value: 'ALL', label: t('assignmentFilter.all') },
            ...(currentUser?.assignments || []).map((assignment) => ({
              value: assignment.id,
              label: assignment.label,
            })),
          ]}
        />
      </Space>
      <IncidentListTable
        incidents={displayedIncidents}
        attachmentsByIncident={attachmentsByIncident}
        tasksByIncident={tasksByIncident}
        currentUser={currentUser}
        persistFiltersInUrl
        serverKeywordFiltering={Boolean(fullTextQuery)}
        loading={searchLoading}
      />
    </div>
  );
};

export default IncidentListPage;
